/**
 * Custom hook for WebSocket chat events
 * Provides real-time chat functionality with typing indicators and response handling
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import socketService, { ChatEvent } from '../services/SocketService';
import { getTabId } from '../utils/tabId';

interface ChatMessage {
  id: string;
  content: string;
  role: string; // Allow any role for legacy compatibility ('use', 'sim', 'user', 'assistant', etc.)
  timestamp: string;
  chat_type?: 'persona' | 'simulation' | 'group';
  media?: {
    images?: string[];
    videos?: string[];
  };
}

interface ChatState {
  messages: ChatMessage[];
  isTyping: boolean;
  isSending: boolean;
  isConnected: boolean;
  error: string | null;
  activeChatId: string | null;
  isConsolidating: boolean;
  consolidationMessage: string | null;
}

interface UseChatSocketOptions {
  onMessageReceived?: (data: ChatEvent) => void;
  onResponseStarted?: (data: ChatEvent) => void;
  onResponseCompleted?: (data: ChatEvent) => void;
  onResponseFailed?: (data: ChatEvent) => void;
  onConnectionStatus?: (connected: boolean) => void;
  onPersonaConsolidationStarted?: (data: ChatEvent) => void;
  onPersonaConsolidationCompleted?: (data: ChatEvent) => void;
  autoConnect?: boolean;
  chatId?: string;
  // Chat isolation options
  chatType?: 'persona' | 'simulation' | 'group';
  simulationId?: number;
  personaId?: number;
  personaIds?: number[];
}

interface UseChatSocketReturn {
  chatState: ChatState;
  sendMessage: (message: string, chatType: 'persona' | 'simulation' | 'group', options?: any) => Promise<string>;
  addOptimisticMessage: (message: string, chatType: 'persona' | 'simulation' | 'group', media?: { images?: string[], videos?: string[] }) => string;
  removeOptimisticMessage: (messageId: string) => void;
  clearChat: () => void;
  connect: () => Promise<void>;
  disconnect: () => void;
  setActiveChatId: (chatId: string | null) => void;
  isConnected: boolean;
  connectionStatus: 'connected' | 'connecting' | 'disconnected';
}

// Helper function to generate chat identifier for isolation
const generateChatIdentifier = (
  chatType: 'persona' | 'simulation' | 'group',
  simulationId?: number,
  personaId?: number,
  personaIds?: number[]
): string => {
  switch (chatType) {
    case 'persona':
      return `persona_${simulationId}_${personaId}`;
    case 'simulation':
      return `simulation_${simulationId}`;
    case 'group':
      return `group_${personaIds?.sort().join('_') || 'unknown'}`;
    default:
      return 'unknown';
  }
};

export const useChatSocket = (
  options: UseChatSocketOptions = {}
): UseChatSocketReturn => {
  const {
    onMessageReceived,
    onResponseStarted,
    onResponseCompleted,
    onResponseFailed,
    onConnectionStatus,
    onPersonaConsolidationStarted,
    onPersonaConsolidationCompleted,
    autoConnect = true,
    chatId,
    chatType,
    simulationId,
    personaId,
    personaIds
  } = options;

  // Generate chat identifier for message isolation
  const currentChatIdentifier = chatType 
    ? (() => {
        
        // For persona and simulation chats, we need simulationId
        if ((chatType === 'persona' || chatType === 'simulation') && simulationId) {
          const identifier = generateChatIdentifier(chatType, simulationId, personaId, personaIds);
          return identifier;
        }
        // For group chat, we need personaIds
        if (chatType === 'group' && personaIds && personaIds.length > 0) {
          const identifier = generateChatIdentifier(chatType, simulationId, personaId, personaIds);
          return identifier;
        }
        return null;
      })()
    : null;


  const [chatState, setChatState] = useState<ChatState>({
    messages: [],
    isTyping: false,
    isSending: false,
    isConnected: false,
    error: null,
    activeChatId: chatId || currentChatIdentifier,
    isConsolidating: false,
    consolidationMessage: null
  });

  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected'>('disconnected');
  const subscriberIdRef = useRef<string | null>(null);
  const componentIdRef = useRef(`chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const tabIdRef = useRef(getTabId());
  const isInitialMount = useRef(true);
  const callbacksRef = useRef({ 
    onMessageReceived, 
    onResponseStarted, 
    onResponseCompleted, 
    onResponseFailed, 
    onConnectionStatus,
    onPersonaConsolidationStarted,
    onPersonaConsolidationCompleted
  });

  // Update callbacks ref when they change
  useEffect(() => {
    callbacksRef.current = { 
      onMessageReceived, 
      onResponseStarted, 
      onResponseCompleted, 
      onResponseFailed, 
      onConnectionStatus,
      onPersonaConsolidationStarted,
      onPersonaConsolidationCompleted
    };
  }, [onMessageReceived, onResponseStarted, onResponseCompleted, onResponseFailed, onConnectionStatus, onPersonaConsolidationStarted, onPersonaConsolidationCompleted]);

  // Update active chat ID when chatId prop or chat identifier changes
  useEffect(() => {
    setChatState(prev => ({ ...prev, activeChatId: chatId || currentChatIdentifier }));
  }, [chatId, currentChatIdentifier]);

  // Clear chat when simulation/persona changes
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Clear chat state when simulation or persona changes
    setChatState(prev => ({
      ...prev,
      messages: [],
      isTyping: false,
      isSending: false,
      error: null,
      isConsolidating: false,
      consolidationMessage: null
    }));
  }, [simulationId, personaId, chatType]);

  // Helper function to check if message belongs to current chat
  const belongsToCurrentChat = useCallback((eventChatId: string, eventTabId?: string): boolean => {
    // Strict tab ID checking
    if (tabIdRef.current) {
      // If we have a tab ID, event MUST have matching tab ID
      if (!eventTabId || eventTabId !== tabIdRef.current) {
        return false;
      }
    }
    
    // If we don't have a specific chat identifier, don't accept any messages
    // This prevents unintended message reception
    if (!currentChatIdentifier) {
      return false;
    }
    
    // If eventChatId is undefined or null, reject
    if (!eventChatId) {
      return false;
    }
    
    // Check if the event chat_id matches our current chat pattern
    if (currentChatIdentifier && eventChatId) {
      // Backend format: chat_{type}_{id}_{id}_{user_id}_{timestamp}
      // Frontend format: {type}_{id}_{id}
      
      const eventParts = eventChatId.split('_');
      
      // Extract chat type from backend format (skip 'chat' prefix)
      const eventType = eventParts[1]; // 'persona', 'simulation', or 'group'
      
      
      // Check based on our current chat type
      if (currentChatIdentifier.startsWith('persona_')) {
        // For persona chat: check if event is persona type and matches simulation and persona IDs
        if (eventType === 'persona' && eventParts.length >= 5) {
          const eventSimId = eventParts[2];
          const eventPersonaId = eventParts[3];
          const currentParts = currentChatIdentifier.split('_');
          const currentSimId = currentParts[1];
          const currentPersonaId = currentParts[2];
          const matches = eventSimId === currentSimId && eventPersonaId === currentPersonaId;
          return matches;
        } else {
        }
      } else if (currentChatIdentifier.startsWith('simulation_')) {
        
        // For simulation chat: check if event is simulation type and matches simulation ID
        // Backend format: chat_simulation_{user_id}_{simulation_id}_{random}
        // So simulation_id is at index 3, not 2
        if (eventType === 'simulation' && eventParts.length >= 5) {
          const eventSimId = eventParts[3]; // Changed from eventParts[2] to eventParts[3]
          const currentParts = currentChatIdentifier.split('_');
          const currentSimId = currentParts[1];
          
          
          // Convert to strings for comparison to handle any type mismatches
          const eventSimIdStr = String(eventSimId);
          const currentSimIdStr = String(currentSimId);
          const matches = eventSimIdStr === currentSimIdStr;
          
          
          if (matches) {
          } else {
          }
          return matches;
        } else {
        }
        return false;
      } else if (currentChatIdentifier.startsWith('group_')) {
        // For group chat: ONLY accept group type messages, NEVER simulation or persona
        if (eventType === 'group' && eventParts.length >= 3) {
          return true;
        } else {
        }
        return false;
      } else {
      }
    } else {
    }
    
    return false;
  }, [currentChatIdentifier]);

  // Chat event handlers
  const handleMessageReceived = useCallback((data: ChatEvent) => {
    
    // Only process if this message belongs to our current chat
    const belongsToChat = belongsToCurrentChat(data.chat_id, (data as any).tab_id);
    
    if (belongsToChat) {
      setChatState(prev => {
        // Handle message content properly - ensure it's a string
        let messageContent = '';
        if (data.message) {
          if (typeof data.message === 'string') {
            messageContent = data.message;
          } else if (typeof data.message === 'object') {
            const msgObj = data.message as any;
            messageContent = msgObj.text || msgObj.content || msgObj.message || JSON.stringify(data.message);
          } else {
            messageContent = String(data.message);
          }
        }
        
        // Check if we already have this exact message (prevent duplicates)
        const existingMessage = prev.messages.find(
          msg => msg.content === messageContent && 
                 (msg.role === 'user' || msg.role === 'use' || msg.role === 'use_persona') &&
                 !msg.id.startsWith('optimistic-')
        );
        
        if (existingMessage) {
          return prev;
        }
        
        // Check if we already have an optimistic message with the same content
        // Check for any user-type role since we use legacy role names
        const optimisticMessageIndex = prev.messages.findIndex(
          msg => msg.content === messageContent && 
                 (msg.role === 'user' || msg.role === 'use' || msg.role === 'use_persona') && 
                 msg.id.startsWith('optimistic-')
        );
        
        if (optimisticMessageIndex !== -1) {
          // Replace the optimistic message with the real WebSocket message
          // IMPORTANT: Preserve the media from the optimistic message
          const optimisticMessage = prev.messages[optimisticMessageIndex];
          
          // Map to legacy role based on chat_type
          let role: string;
          if (data.chat_type === 'simulation') {
            role = 'use';
          } else if (data.chat_type === 'persona') {
            role = 'use_persona';
          } else {
            role = 'user';
          }
          
          const newMessage = {
            id: `${componentIdRef.current}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-user`,
            content: messageContent,
            role: role,
            timestamp: data.timestamp,
            chat_type: data.chat_type,
            ...(optimisticMessage.media && { media: optimisticMessage.media }) // Preserve media from optimistic message
          };
          
          const updatedMessages = [...prev.messages];
          updatedMessages[optimisticMessageIndex] = newMessage;
          
          return {
            ...prev,
            messages: updatedMessages,
            activeChatId: data.chat_id,
            error: null
          };
        } else {
          // No optimistic message found, add new message normally
          
          // Map to legacy role based on chat_type
          let role: string;
          if (data.chat_type === 'simulation') {
            role = 'use';
          } else if (data.chat_type === 'persona') {
            role = 'use_persona';
          } else {
            role = 'user';
          }
          
          const newMessage = {
            id: `${componentIdRef.current}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-user`,
            content: messageContent,
            role: role,
            timestamp: data.timestamp,
            chat_type: data.chat_type
          };
          
          const newState = {
            ...prev,
            messages: [...prev.messages, newMessage],
            activeChatId: data.chat_id,
            error: null
          };
          return newState;
        }
      });
    } else {
    }
    
    callbacksRef.current.onMessageReceived?.(data);
  }, [belongsToCurrentChat]);

  const handleResponseStarted = useCallback((data: ChatEvent) => {
    
    // Only process if this message belongs to our current chat
    const belongsToChat = belongsToCurrentChat(data.chat_id, (data as any).tab_id);
    
    if (belongsToChat) {
      setChatState(prev => ({
        ...prev,
        isTyping: true,
        isSending: false,  // Clear sending state when response starts
        error: null,
        activeChatId: data.chat_id
      }));
    } else {
    }
    
    callbacksRef.current.onResponseStarted?.(data);
  }, [belongsToCurrentChat]);

  const handleResponseCompleted = useCallback((data: ChatEvent) => {
    
    // Only process if this message belongs to our current chat
    const belongsToChat = belongsToCurrentChat(data.chat_id, (data as any).tab_id);
    
    if (belongsToChat) {
      setChatState(prev => {
        // Handle response content properly - ensure it's a string
        // Backend might send response as object in some cases
        let responseContent = '';
        if (data.response) {
          if (typeof data.response === 'string') {
            responseContent = data.response;
          } else if (typeof data.response === 'object') {
            // Try to extract text/content from object, or stringify it
            const respObj = data.response as any;
            responseContent = respObj.text || respObj.content || respObj.message || respObj.response || JSON.stringify(data.response);
          } else {
            responseContent = String(data.response);
          }
        }
        
        // Check if we already have this exact response (prevent duplicates)
        const existingResponse = prev.messages.find(
          msg => msg.content === responseContent && 
                 (msg.role === 'assistant' || msg.role === 'sim' || msg.role === 'persona')
        );
        
        if (existingResponse) {
          return prev;
        }
        
        // Map to legacy role based on chat_type
        let role: string;
        if (data.chat_type === 'simulation') {
          role = 'sim'; // Legacy role for simulation AI responses
        } else if (data.chat_type === 'persona') {
          role = 'persona'; // Legacy role for persona AI responses
        } else {
          role = 'assistant'; // Standard role for other chat types
        }
        
        const newMessage = {
          id: `${componentIdRef.current}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-assistant`,
          content: responseContent,
          role: role,
          timestamp: data.timestamp,
          chat_type: data.chat_type
        };
        
        const newState = {
          ...prev,
          messages: [...prev.messages, newMessage],
          isTyping: false,
          isSending: false,  // Clear sending state when response completes
          error: null,
          activeChatId: data.chat_id
        };
        return newState;
      });
    } else {
    }
    
    callbacksRef.current.onResponseCompleted?.(data);
  }, [belongsToCurrentChat]);

  const handleResponseFailed = useCallback((data: ChatEvent) => {
    console.error('❌ Chat response failed:', data);
    
    // Only process if this message belongs to our current chat
    if (belongsToCurrentChat(data.chat_id, (data as any).tab_id)) {
      setChatState(prev => ({
        ...prev,
        isTyping: false,
        isSending: false,  // Clear sending state on failure
        error: data.error || data.detail || 'Chat response failed',
        activeChatId: data.chat_id
      }));
    }
    
    callbacksRef.current.onResponseFailed?.(data);
  }, [belongsToCurrentChat]);

  const handleConnectionStatus = useCallback((connected: boolean) => {
    setChatState(prev => ({ ...prev, isConnected: connected }));
    setConnectionStatus(connected ? 'connected' : 'disconnected');
    callbacksRef.current.onConnectionStatus?.(connected);
  }, []);

  const handlePersonaConsolidationStarted = useCallback((data: ChatEvent) => {
    
    // Only process if this message belongs to our current chat
    if (belongsToCurrentChat(data.chat_id, (data as any).tab_id)) {
      setChatState(prev => ({
        ...prev,
        isConsolidating: true,
        consolidationMessage: data.message || "The first response may take a couple of minutes, but after that everything runs much faster.",
        error: null,
        activeChatId: data.chat_id
      }));
    }
    
    callbacksRef.current.onPersonaConsolidationStarted?.(data);
  }, [belongsToCurrentChat]);

  const handlePersonaConsolidationCompleted = useCallback((data: ChatEvent) => {
    
    // Only process if this message belongs to our current chat
    if (belongsToCurrentChat(data.chat_id, (data as any).tab_id)) {
      setChatState(prev => ({
        ...prev,
        isConsolidating: false,
        consolidationMessage: null,
        error: null,
        activeChatId: data.chat_id
      }));
    }
    
    callbacksRef.current.onPersonaConsolidationCompleted?.(data);
  }, [belongsToCurrentChat]);

  // Auto-connect and re-subscribe when chat identifier changes
  useEffect(() => {
    if (!autoConnect) {
      return () => {
        // Unsubscribe this specific subscriber on cleanup
        if (subscriberIdRef.current) {
          socketService.unsubscribeSubscriber(subscriberIdRef.current);
          subscriberIdRef.current = null;
        }
      };
    }
    
    // Clean up existing subscription before creating new one
    if (subscriberIdRef.current) {
      socketService.unsubscribeSubscriber(subscriberIdRef.current);
      subscriberIdRef.current = null;
    }
    
    const timer = setTimeout(() => {
      try {
        // Re-establish connection with new chat identifier
        connect().catch(error => {
          console.error('WebSocket connection error:', error);
          setConnectionStatus('disconnected');
        });
      } catch (error) {
        console.error('WebSocket setup error:', error);
        setConnectionStatus('disconnected');
      }
    }, 250);
    
    return () => {
      clearTimeout(timer);
      // Unsubscribe this specific subscriber on cleanup
      if (subscriberIdRef.current) {
        socketService.unsubscribeSubscriber(subscriberIdRef.current);
        subscriberIdRef.current = null;
      }
    };
  }, [autoConnect, currentChatIdentifier]); // Re-run when chat identifier changes

  // Connect function
  const connect = async (): Promise<void> => {
    try {
      setConnectionStatus('connecting');
      
      await socketService.connect();
      
      // Clean up any existing subscription before creating new one
      if (subscriberIdRef.current) {
        socketService.unsubscribeSubscriber(subscriberIdRef.current);
        subscriberIdRef.current = null;
      }
      
      const subscriberId = socketService.subscribeToEvents({
        onChatMessageReceived: handleMessageReceived,
        onChatResponseStarted: handleResponseStarted,
        onChatResponseCompleted: handleResponseCompleted,
        onChatResponseFailed: handleResponseFailed,
        onConnectionStatus: handleConnectionStatus,
        onPersonaConsolidationStarted: handlePersonaConsolidationStarted,
        onPersonaConsolidationCompleted: handlePersonaConsolidationCompleted
      });
      subscriberIdRef.current = subscriberId;

      setChatState(prev => ({ ...prev, isConnected: true }));
      setConnectionStatus('connected');
    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
      setChatState(prev => ({ ...prev, isConnected: false }));
      setConnectionStatus('disconnected');
      throw error;
    }
  };

  // Disconnect function
  const disconnect = (): void => {
    // Unsubscribe this specific subscriber
    if (subscriberIdRef.current) {
      socketService.unsubscribeSubscriber(subscriberIdRef.current);
      subscriberIdRef.current = null;
    }
    
    socketService.disconnect();
    setChatState(prev => ({ 
      ...prev, 
      isConnected: false,
      isTyping: false,
      isSending: false,
      error: null
    }));
    setConnectionStatus('disconnected');
  };

  // Send message function
  const sendMessage = async (
    message: string, 
    chatType: 'persona' | 'simulation' | 'group',
    options: any = {}
  ): Promise<string> => {
    if (!message.trim()) {
      throw new Error('Message cannot be empty');
    }

    if (!socketService.isConnected()) {
      throw new Error('Not connected to chat service');
    }

    try {
      // Set sending state immediately
      setChatState(prev => ({ ...prev, isSending: true, error: null }));
      
      const apiUrl = import.meta.env.VITE_API_URL || '';
      let endpoint = '';
      let payload: any = { message, tab_id: tabIdRef.current };

      // Determine the correct endpoint based on chat type
      switch (chatType) {
        case 'persona':
          endpoint = `/chat/async/simulation/${options.simulationId}/persona/${options.personaId}`;
          // Include media if provided
          if (options.images) payload.images = options.images;
          if (options.videos) payload.videos = options.videos;
          break;
        case 'simulation':
          endpoint = `/chat/async/simulation/${options.simulationId}`;
          // Include media if provided
          if (options.images) payload.images = options.images;
          if (options.videos) payload.videos = options.videos;
          break;
        case 'group':
          endpoint = '/persona_group_chat_async';
          payload = {
            query: message,
            persona_ids: options.personaIds || [],
            chat_history_id: options.chatHistoryId,
            consolidated_personas_json: options.consolidatedPersonasJson,
            media_context: options.mediaContext || [],
            tab_id: tabIdRef.current,
            // Include media if provided
            ...(options.images && { images: options.images }),
            ...(options.videos && { videos: options.videos })
          };
          break;
        default:
          throw new Error(`Invalid chat type: ${chatType}`);
      }

      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.detail || 'Failed to send message');
      }

      const result = await response.json();
      console.log(`📬 Response from ${endpoint}:`, result);
      
      // If using polling mode, start polling for this chat
      if (result.chat_id && socketService.isPollingMode()) {
        console.log(`🚀 Starting polling for chat_id: ${result.chat_id}`);
        socketService.handleChatSubmitted(result.chat_id, tabIdRef.current || undefined);
      }
      
      return result.chat_id;

    } catch (error) {
      console.error('Error sending message:', error);
      setChatState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Failed to send message',
        isTyping: false,
        isSending: false  // Clear sending state on error
      }));
      throw error;
    }
  };

  // Clear chat function
  const clearChat = (): void => {
    setChatState(prev => ({
      ...prev,
      messages: [],
      isTyping: false,
      isSending: false,
      error: null,
      isConsolidating: false,
      consolidationMessage: null
    }));
  };

  // Set active chat ID function
  const setActiveChatId = (newChatId: string | null): void => {
    setChatState(prev => ({ ...prev, activeChatId: newChatId }));
  };

  // Add optimistic message function for immediate UI updates
  const addOptimisticMessage = (message: string, chatType: 'persona' | 'simulation' | 'group', media?: { images?: string[], videos?: string[] }): string => {
    const messageId = `optimistic-${componentIdRef.current}-${Date.now()}-${Math.random()}`;
    
    // Use legacy role names for compatibility with existing UI components
    let role: string;
    switch (chatType) {
      case 'simulation':
        role = 'use'; // Legacy role for simulation user messages
        break;
      case 'persona':
        role = 'use_persona'; // Legacy role for persona user messages
        break;
      case 'group':
        role = 'user'; // Group chat uses standard role
        break;
      default:
        role = 'user';
    }
    
    const newMessage = {
      id: messageId,
      content: message,
      role: role,
      timestamp: new Date().toISOString(),
      chat_type: chatType,
      ...(media && { media }) // Include media if provided
    };

    
    setChatState(prev => ({
      ...prev,
      messages: [...prev.messages, newMessage],
      error: null
    }));

    return messageId;
  };

  // Remove optimistic message function for error handling
  const removeOptimisticMessage = (messageId: string): void => {
    
    setChatState(prev => ({
      ...prev,
      messages: prev.messages.filter(msg => msg.id !== messageId)
    }));
  };

  return {
    chatState,
    sendMessage,
    addOptimisticMessage,
    removeOptimisticMessage,
    clearChat,
    connect,
    disconnect,
    setActiveChatId,
    isConnected: chatState.isConnected,
    connectionStatus
  };
};

export default useChatSocket;